import { useState } from 'react';
import type { CalendarException, CalendarRecurrenceKind } from '@bigint-project/core';
import { isSyscalId } from '@bigint-project/calendar-catalog';
import { hhmmToMinutes, minutesToHHmm, workingTime } from './time-format.js';
import { TAIWAN_FIXED_HOLIDAYS, fixedHolidayToException } from './taiwan-fixed-holidays.js';

export interface ExceptionListEditorProps {
  readonly exceptions: readonly CalendarException[];
  readonly onChange: (next: readonly CalendarException[]) => void;
}

const RECURRENCE_LABELS: Record<CalendarRecurrenceKind, string> = {
  None: '單次',
  Daily: '每日重複',
  Weekly: '每週重複',
  Monthly: '每月重複',
  Yearly: '每年重複',
};

interface DraftState {
  readonly name: string;
  readonly from: string;
  readonly to: string;
  readonly working: boolean;
  readonly halfDay: boolean;
  readonly halfStart: string;
  readonly halfEnd: string;
  readonly recurrence: CalendarRecurrenceKind;
  readonly interval: string;
  readonly until: string;
}

const emptyDraft = (): DraftState => ({
  name: '',
  from: '',
  to: '',
  working: false,
  halfDay: false,
  halfStart: '09:00',
  halfEnd: '12:00',
  recurrence: 'None',
  interval: '1',
  until: '',
});

function makeId(): string {
  return `exc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Catalog integrity requires every manual exception to precede the system-managed suffix. */
function appendManualExceptions(
  exceptions: readonly CalendarException[],
  additions: readonly CalendarException[],
): readonly CalendarException[] {
  const firstSystemIndex = exceptions.findIndex((exception) => isSyscalId(exception.id));
  if (firstSystemIndex === -1) return [...exceptions, ...additions];
  return [
    ...exceptions.slice(0, firstSystemIndex),
    ...additions,
    ...exceptions.slice(firstSystemIndex),
  ];
}

function describeDate(exception: CalendarException): string {
  const range = exception.from === exception.to ? exception.from : `${exception.from} ~ ${exception.to}`;
  const rec = exception.recurrence;
  if (rec === undefined || rec.kind === 'None') return range;
  const interval = rec.interval > 1 ? `每 ${rec.interval} ` : '每';
  const unit = { Daily: '天', Weekly: '週', Monthly: '月', Yearly: '年' }[rec.kind as 'Daily' | 'Weekly' | 'Monthly' | 'Yearly'];
  const untilText = rec.until !== undefined ? `至 ${rec.until}` : '';
  return `${range}（${interval}${unit}重複${untilText ? `，${untilText}` : ''}）`;
}

/**
 * 例外日（假日／特殊工時）清單維護：新增單日／區間／週期性例外、編輯、刪除，依日期排序。
 * 純展示元件：props in / callbacks out。零 store、零 Tauri 相依。
 */
export function ExceptionListEditor({ exceptions, onChange }: ExceptionListEditorProps): React.JSX.Element {
  const [draft, setDraft] = useState<DraftState>(emptyDraft());
  const [showForm, setShowForm] = useState(false);

  const sorted = [...exceptions].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));

  const removeException = (id: string): void => {
    if (isSyscalId(id)) return;
    onChange(exceptions.filter((e) => e.id !== id));
  };

  const submitDraft = (): void => {
    if (draft.name.trim() === '' || draft.from === '') return;
    const to = draft.to === '' ? draft.from : draft.to;
    const workingTimes = draft.working && draft.halfDay
      ? (() => {
          const start = hhmmToMinutes(draft.halfStart);
          const end = hhmmToMinutes(draft.halfEnd);
          if (start === null || end === null || end <= start) return [];
          return [workingTime(start, end)];
        })()
      : [];
    const interval = Math.max(1, Math.floor(Number(draft.interval) || 1));
    const exception: CalendarException = {
      id: makeId(),
      name: draft.name.trim(),
      from: draft.from as CalendarException['from'],
      to: to as CalendarException['to'],
      working: draft.working,
      workingTimes,
      recurrence:
        draft.recurrence === 'None'
          ? undefined
          : {
              kind: draft.recurrence,
              interval,
              until: draft.until === '' ? undefined : (draft.until as NonNullable<CalendarException['recurrence']>['until']),
            },
    };
    onChange(appendManualExceptions(exceptions, [exception]));
    setDraft(emptyDraft());
    setShowForm(false);
  };

  const addFixedHolidays = (): void => {
    const year = new Date().getFullYear();
    const existingNames = new Set(exceptions.map((e) => e.name));
    const additions = TAIWAN_FIXED_HOLIDAYS.filter((h) => !existingNames.has(h.name)).map((h) =>
      fixedHolidayToException(h, year),
    );
    if (additions.length === 0) return;
    onChange(appendManualExceptions(exceptions, additions));
  };

  return (
    <div data-testid="exception-list-editor" className="text-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium text-fg">例外日清單</span>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="exception-add-fixed-holidays"
            title="僅新增日期固定的國定假日（元旦／和平紀念日／兒童節／勞動節／國慶日）。農曆節日與補班／彈性放假逐年不同，請自行新增。"
            className="rounded border border-line-strong px-2 py-1 text-xs text-fg hover:bg-raised"
            onClick={addFixedHolidays}
          >
            + 新增常見固定節日
          </button>
          <button
            type="button"
            data-testid="exception-toggle-form"
            className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? '取消' : '+ 新增例外'}
          </button>
        </div>
      </div>

      <p className="mb-2 text-xs text-fg-muted">
        提醒：農曆節日（春節、端午、中秋等）與補班、彈性放假逐年由政府公告，日期不固定，請依當年公告手動新增，不建議寫死。
      </p>

      {showForm ? (
        <div data-testid="exception-form" className="mb-3 space-y-2 rounded border border-line p-2">
          <div className="flex flex-wrap gap-2">
            <label className="flex flex-col text-xs text-fg-muted">
              名稱
              <input
                data-testid="exception-form-name"
                className="rounded border border-line-strong px-1.5 py-1 text-sm text-fg"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-xs text-fg-muted">
              起始日
              <input
                type="date"
                data-testid="exception-form-from"
                className="rounded border border-line-strong px-1.5 py-1 text-sm text-fg"
                value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-xs text-fg-muted">
              結束日（區間，留空同起始日）
              <input
                type="date"
                data-testid="exception-form-to"
                className="rounded border border-line-strong px-1.5 py-1 text-sm text-fg"
                value={draft.to}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1 text-xs text-fg-muted">
              <input
                type="checkbox"
                data-testid="exception-form-working"
                checked={draft.working}
                onChange={(e) => setDraft({ ...draft, working: e.target.checked, halfDay: e.target.checked ? draft.halfDay : false })}
              />
              當日仍工作（例如補班／半天班），取消勾選代表全日放假
            </label>
            {draft.working ? (
              <label className="flex items-center gap-1 text-xs text-fg-muted">
                <input
                  type="checkbox"
                  data-testid="exception-form-halfday"
                  checked={draft.halfDay}
                  onChange={(e) => setDraft({ ...draft, halfDay: e.target.checked })}
                />
                自訂當日時段（例如半天班）
              </label>
            ) : null}
            {draft.working && draft.halfDay ? (
              <span className="flex items-center gap-1 text-xs">
                <input
                  aria-label="例外時段開始"
                  data-testid="exception-form-half-start"
                  className="w-16 rounded border border-line-strong px-1 py-0.5"
                  defaultValue={draft.halfStart}
                  onBlur={(e) => setDraft({ ...draft, halfStart: e.target.value })}
                />
                –
                <input
                  aria-label="例外時段結束"
                  data-testid="exception-form-half-end"
                  className="w-16 rounded border border-line-strong px-1 py-0.5"
                  defaultValue={draft.halfEnd}
                  onBlur={(e) => setDraft({ ...draft, halfEnd: e.target.value })}
                />
              </span>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="flex flex-col text-xs text-fg-muted">
              週期
              <select
                data-testid="exception-form-recurrence"
                className="rounded border border-line-strong px-1.5 py-1 text-sm text-fg"
                value={draft.recurrence}
                onChange={(e) => setDraft({ ...draft, recurrence: e.target.value as CalendarRecurrenceKind })}
              >
                {(Object.keys(RECURRENCE_LABELS) as CalendarRecurrenceKind[]).map((kind) => (
                  <option key={kind} value={kind}>
                    {RECURRENCE_LABELS[kind]}
                  </option>
                ))}
              </select>
            </label>
            {draft.recurrence !== 'None' ? (
              <>
                <label className="flex flex-col text-xs text-fg-muted">
                  間隔
                  <input
                    type="number"
                    min={1}
                    data-testid="exception-form-interval"
                    className="w-16 rounded border border-line-strong px-1.5 py-1 text-sm text-fg"
                    value={draft.interval}
                    onChange={(e) => setDraft({ ...draft, interval: e.target.value })}
                  />
                </label>
                <label className="flex flex-col text-xs text-fg-muted">
                  重複結束日（留空＝無限）
                  <input
                    type="date"
                    data-testid="exception-form-until"
                    className="rounded border border-line-strong px-1.5 py-1 text-sm text-fg"
                    value={draft.until}
                    onChange={(e) => setDraft({ ...draft, until: e.target.value })}
                  />
                </label>
              </>
            ) : null}
          </div>

          <button
            type="button"
            data-testid="exception-form-submit"
            className="rounded bg-blue-600 px-3 py-1 text-xs text-white hover:bg-blue-700"
            onClick={submitDraft}
          >
            儲存例外
          </button>
        </div>
      ) : null}

      <ul className="max-h-64 space-y-1 overflow-y-auto">
        {sorted.length === 0 ? (
          <li className="py-2 text-xs text-fg-subtle">尚未設定例外日</li>
        ) : (
          sorted.map((exception) => {
            const isSystemManaged = isSyscalId(exception.id);
            return (
              <li
                key={exception.id}
                data-testid={`exception-row-${exception.id}`}
                aria-readonly={isSystemManaged}
                className="flex items-center justify-between gap-2 rounded border border-hairline px-2 py-1.5"
              >
                <div>
                  <span className="font-medium text-fg">{exception.name}</span>
                  {isSystemManaged ? (
                    <span
                      data-testid={`exception-system-managed-${exception.id}`}
                      className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700 dark:bg-accent-soft dark:text-accent"
                      title="由共享行事曆版本管理；一般編輯器不可修改或刪除"
                    >
                      系統管理（唯讀）
                    </span>
                  ) : null}
                  <span className="ml-2 text-xs text-fg-muted">{describeDate(exception)}</span>
                  <span
                    className={`ml-2 rounded px-1.5 py-0.5 text-xs ${
                      exception.working
                        ? 'bg-ok-soft text-ok'
                        : 'bg-danger-soft text-danger'
                    }`}
                  >
                    {exception.working
                      ? exception.workingTimes.length > 0
                        ? `工作 ${exception.workingTimes.map((t) => `${minutesToHHmm(t.start as number)}–${minutesToHHmm(t.end as number)}`).join('、')}`
                        : '工作（沿用當日時段）'
                      : '不工作'}
                  </span>
                </div>
                <button
                  type="button"
                  aria-label={`刪除 ${exception.name}`}
                  className="text-danger hover:opacity-75 disabled:cursor-not-allowed disabled:opacity-30"
                  disabled={isSystemManaged}
                  title={isSystemManaged ? '系統管理的例外日不可由一般編輯器刪除' : undefined}
                  onClick={() => removeException(exception.id)}
                >
                  ×
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
